// src/components/hall-prices/CreateAdminOrderModal.tsx
import React, { useEffect, useMemo, useState } from "react";
import Button from "../ui/Button";
import Input from "../ui/Input";
import Field from "../ui/Field";
import Select from "../ui/Select";
import { getUsersRequest, UserItem } from "../../services/api/users.api";
import {
    adminCreateOrderRequest,
    AdminOrderCreatePayload,
    partnerReserveOrderRequest,
    partnerConfirmOrderRequest,
    partnerCancelOrderRequest,
    PartnerOrderReservePayload,
} from "../../services/api/orders.api";
import { getDiscountsRequest } from "../../services/api/event-content.api";
import { DiscountItem } from "../../services/types/event-content.types";
import { OrderItem } from "../../services/types/orders.types";

type SelectedSeatInfo = {
    hall_seat_price_id: string;
    label: string;
    price: number;
};

type Props = {
    isOpen: boolean;
    /** id записей hall_seat_prices */
    selectedSeatPriceIds: string[];
    /** те же места, но с человеческим описанием и ценой — для скидок по билетам */
    selectedSeatsInfo?: SelectedSeatInfo[];
    /** organizer_id тура — скидки по билетам ("rabatt") доступны только для одного организатора */
    tourOrganizerId?: string | null;
    /** Тур с партнёром (Artist Production) — вместо обычного создания заказа делается
     * только резерв через API партнёра; оплата/подтверждение у партнёра отмечаются позже
     * обычным внесением оплаты в списке заказов. */
    isPartnerTour?: boolean;
    /** Вызывается сразу после успешного резерва у партнёра (модалка ещё открыта) —
     * чтобы родительский компонент сразу обновил схему и визуально заблокировал занятые места. */
    onReserved?: () => void;
    onClose: () => void;
    onCreated: () => void;
};

const LATIN_RE = /^[A-Za-zÄÖÜäöüß\s'’\-]+$/;

// Скидки по билетам ("rabatt") доступны только для мероприятий этого организатора —
// точно такое же ограничение, как на сайте и на бэкенде.
const RABATT_ALLOWED_ORGANIZER_ID = "4beb5c6e-12be-44a9-909f-68e6b975a0fc";

const CreateAdminOrderModal: React.FC<Props> = ({
    isOpen,
    selectedSeatPriceIds,
    selectedSeatsInfo,
    tourOrganizerId,
    isPartnerTour,
    onReserved,
    onClose,
    onCreated,
}) => {
    // Партнёрский флоу: после успешного резерва тут же хранится созданный (ещё не
    // оплаченный) заказ — модалка переключается на шаг подтверждения/отмены.
    const [pendingPartnerOrder, setPendingPartnerOrder] = useState<OrderItem | null>(null);
    // поля формы (новый клиент)
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [phone, setPhone] = useState("");
    const [isInvite, setIsInvite] = useState(false);
    const [paymentType, setPaymentType] =
        useState<"cash" | "rechnung" | "stripe">("cash");

    // Доставка
    const [needDelivery, setNeedDelivery] = useState(false);
    const [country, setCountry] = useState("");
    const [city, setCity] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [address, setAddress] = useState("");

    // autocomplete существующего клиента
    const [emailQuery, setEmailQuery] = useState("");
    const [emailResults, setEmailResults] = useState<UserItem[]>([]);
    const [emailLoading, setEmailLoading] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState<UserItem | null>(null);
    const [showDropdown, setShowDropdown] = useState(false);

    // состояние сохранения
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // скидки по билетам ("rabatt"): hall_seat_price_id -> discount_id
    const [discounts, setDiscounts] = useState<DiscountItem[]>([]);
    const [seatDiscounts, setSeatDiscounts] = useState<Record<string, string>>({});
    const [discountModalSeatId, setDiscountModalSeatId] = useState<string | null>(null);
    // отдельное окно со списком всех билетов и их скидками — открывается над окном создания заказа,
    // чтобы его собственный список мог прокрутиваться независимо от основной формы.
    const [isSeatsDiscountsModalOpen, setIsSeatsDiscountsModalOpen] = useState(false);

    // сброс при открытии
    useEffect(() => {
        if (!isOpen) return;
        setPendingPartnerOrder(null);
        setFirstName("");
        setLastName("");
        setEmail("");
        setPassword("");
        setPhone("");
        setIsInvite(false);
        setPaymentType("cash");
        setNeedDelivery(false);
        setCountry("");
        setCity("");
        setZipCode("");
        setAddress("");
        setEmailQuery("");
        setEmailResults([]);
        setSelectedCustomer(null);
        setShowDropdown(false);
        setError(null);
        setSeatDiscounts({});
        setDiscountModalSeatId(null);
        setIsSeatsDiscountsModalOpen(false);
    }, [isOpen]);

    // загрузка активных скидок при открытии модалки
    useEffect(() => {
        if (!isOpen) return;
        getDiscountsRequest()
            .then((items) =>
                setDiscounts(Array.isArray(items) ? items.filter((d) => d.is_active) : []),
            )
            .catch(() => setDiscounts([]));
    }, [isOpen]);

    // debounce-поиск пользователей по email
    useEffect(() => {
        if (!isOpen) return;
        const q = emailQuery.trim();
        if (q.length < 2) {
            setEmailResults([]);
            return;
        }
        let cancelled = false;
        const timer = setTimeout(async () => {
            setEmailLoading(true);
            try {
                const response = await getUsersRequest({
                    email: q,
                    role: "customer",
                    page: 1,
                    page_size: 10,
                    sort_by: "first_name",
                });
                if (cancelled) return;
                const raw: any = response as any;
                const list: UserItem[] = Array.isArray(raw)
                    ? raw
                    : Array.isArray(raw?.items)
                    ? raw.items
                    : Array.isArray(raw?.users)
                    ? raw.users
                    : [];
                setEmailResults(list);
            } catch {
                if (!cancelled) setEmailResults([]);
            } finally {
                if (!cancelled) setEmailLoading(false);
            }
        }, 300);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [emailQuery, isOpen]);

    const handlePickCustomer = (u: UserItem) => {
        setSelectedCustomer(u);
        setFirstName(u.first_name || "");
        setLastName(u.last_name || "");
        setEmail(u.email || "");
        setPhone((u as any)?.customer_profile?.phone || "");
        // пароль не нужен, мы используем существующего
        setPassword("");
        setEmailQuery(u.email || "");
        setShowDropdown(false);
    };

    const handleClearCustomer = () => {
        setSelectedCustomer(null);
        setEmailQuery("");
        setShowDropdown(false);
    };

    const totalLabel = useMemo(() => {
        return isInvite
            ? "Пригласительный (0,00 €)"
            : `${selectedSeatPriceIds.length} мест(а) · сумма посчитается на бэке`;
    }, [isInvite, selectedSeatPriceIds.length]);

    const validateBeforeSubmit = (): string | null => {
        if (selectedSeatPriceIds.length === 0) return "Не выбраны места.";

        if (selectedCustomer) return null; // существующий — данные не нужны

        if (!firstName.trim() || !lastName.trim()) return "Укажите имя и фамилию.";
        if (!LATIN_RE.test(firstName.trim()) || !LATIN_RE.test(lastName.trim()))
            return "Имя и фамилия должны быть только латинскими буквами.";
        if (!email.trim() || !email.includes("@")) return "Укажите корректный email.";
        if (password.length < 8) return "Пароль должен быть не короче 8 символов.";
        return null;
    };

    const validateDelivery = (): string | null => {
        if (!needDelivery) return null;
        if (!country.trim() || !city.trim() || !zipCode.trim() || !address.trim())
            return "Заполните все поля доставки: страна, город, индекс, адрес.";
        return null;
    };

    // --- скидки по билетам ("rabatt") — такая же логика, как на сайте.
    // Доступны и для распространителей — автоматическая агентская скидка больше не применяется,
    // но ручной выбор скидок по билетам доступен им так же, как и обычным клиентам.
    const canApplyDiscounts =
        !isInvite &&
        !isPartnerTour &&
        tourOrganizerId === RABATT_ALLOWED_ORGANIZER_ID &&
        discounts.length > 0 &&
        (selectedSeatsInfo?.length ?? 0) > 0;

    const discountById = useMemo(
        () => new Map(discounts.map((d) => [d.id, d])),
        [discounts],
    );
    const companionDiscountIds = useMemo(
        () => new Set(discounts.filter((d) => d.requires_paid_companion).map((d) => d.id)),
        [discounts],
    );
    const allSeatIds = useMemo(
        () => (selectedSeatsInfo ?? []).map((s) => s.hall_seat_price_id),
        [selectedSeatsInfo],
    );
    // если выбрана скидка с requires_paid_companion — одно из остальных билетов
    // должно остаться без скидки (сопровождающий) — как на сайте.
    const companionLockedSeatId = useMemo(() => {
        const companionSeatEntry = Object.entries(seatDiscounts).find(([, discountId]) =>
            companionDiscountIds.has(discountId),
        );
        if (!companionSeatEntry) return null;
        const [companionSeatId] = companionSeatEntry;
        const freeSeat = allSeatIds.find(
            (id) => id !== companionSeatId && !seatDiscounts[id],
        );
        if (freeSeat) return freeSeat;
        return allSeatIds.find((id) => id !== companionSeatId) ?? null;
    }, [seatDiscounts, companionDiscountIds, allSeatIds]);

    useEffect(() => {
        if (companionLockedSeatId && seatDiscounts[companionLockedSeatId]) {
            setSeatDiscounts((prev) => {
                const next = { ...prev };
                delete next[companionLockedSeatId];
                return next;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companionLockedSeatId]);

    const seatDiscountsTotal = useMemo(() => {
        if (!selectedSeatsInfo || Object.keys(seatDiscounts).length === 0) return 0;
        let sum = 0;
        for (const seat of selectedSeatsInfo) {
            const discountId = seatDiscounts[seat.hall_seat_price_id];
            if (!discountId) continue;
            const discount = discountById.get(discountId);
            if (!discount) continue;
            sum += seat.price * (Number(discount.percent) / 100);
        }
        return Math.round(sum * 100) / 100;
    }, [selectedSeatsInfo, seatDiscounts, discountById]);

    const handleApplyDiscount = (discountId: string | null) => {
        if (!discountModalSeatId) return;
        const seatId = discountModalSeatId;
        setSeatDiscounts((prev) => {
            const next = { ...prev };
            if (discountId) next[seatId] = discountId;
            else delete next[seatId];
            return next;
        });
        setDiscountModalSeatId(null);
    };

    const handleSubmit = async () => {
        const v = validateBeforeSubmit();
        if (v) {
            setError(v);
            return;
        }
        const vd = validateDelivery();
        if (vd) {
            setError(vd);
            return;
        }
        setError(null);
        setIsSaving(true);
        try {
            if (isPartnerTour) {
                // Шаг 1 партнёрского флоу: только резерв (без пригласительных
                // и скидок по билетам — цены фиксированы партнёром). Подтверждение оплаты —
                // отдельный шаг (кнопка ниже, после успешного резерва).
                const reservePayload: PartnerOrderReservePayload = {
                    hall_seat_price_ids: selectedSeatPriceIds,
                    payment_type: paymentType,
                    need_delivery: needDelivery,
                };
                if (needDelivery) {
                    reservePayload.country = country.trim();
                    reservePayload.city = city.trim();
                    reservePayload.zip_code = zipCode.trim();
                    reservePayload.address = address.trim();
                }
                if (selectedCustomer) {
                    reservePayload.customer_id = selectedCustomer.id;
                } else {
                    reservePayload.new_customer = {
                        first_name: firstName.trim(),
                        last_name: lastName.trim(),
                        email: email.trim(),
                        password,
                        phone: phone.trim() || undefined,
                    };
                }
                const order = await partnerReserveOrderRequest(reservePayload);
                setPendingPartnerOrder(order);
                // Модалка остаётся открытой (показываем баннер с кнопкой «Закрыть»), но схему
                // нужно обновить сейчас же, чтобы занятые места сразу отобразились заблокированными.
                onReserved?.();
                return;
            }

            const payload: AdminOrderCreatePayload = {
                hall_seat_price_ids: selectedSeatPriceIds,
                is_invite: isInvite,
                payment_type: isInvite ? "cash" : paymentType,
                need_delivery: needDelivery,
            };
            if (!isInvite && Object.keys(seatDiscounts).length > 0) {
                payload.seat_discounts = seatDiscounts;
            }
            if (needDelivery) {
                payload.country = country.trim();
                payload.city = city.trim();
                payload.zip_code = zipCode.trim();
                payload.address = address.trim();
            }
            if (selectedCustomer) {
                payload.customer_id = selectedCustomer.id;
            } else {
                payload.new_customer = {
                    first_name: firstName.trim(),
                    last_name: lastName.trim(),
                    email: email.trim(),
                    password,
                    phone: phone.trim() || undefined,
                };
            }
            await adminCreateOrderRequest(payload);
            onCreated();
            onClose();
        } catch (err) {
            setError(
                err instanceof Error ? err.message : "Не удалось создать заказ.",
            );
        } finally {
            setIsSaving(false);
        }
    };

    // Закрытие модалки после успешного резерва у партнёра — оплата/подтверждение у партнёра
    // больше не делаются из этой модалки — они отмечаются автоматически, когда админ вносит
    // оплату по этому заказу в списке заказов (обычной кнопкой «Внести оплату»).
    const handleClosePendingPartnerOrder = () => {
        setPendingPartnerOrder(null);
        onCreated();
        onClose();
    };

    // Кнопка/закрытие "Отмена" до резерва (пока заказ ещё не создан) — просто закрываем модалку.
    const handleCloseOrCancel = () => {
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className="modal-backdrop"
            role="dialog"
            aria-modal="true"
            aria-label="Создание заказа администратором"
        >
            <div className="modal" style={{ maxWidth: 560 }}>
                <h3 className="card-title">
                    {isPartnerTour ? "Заказ через партнёра (Artist Production / Nashe)" : "Создать заказ"}
                </h3>
                <p className="card-text">
                    Выбрано мест: <b>{selectedSeatPriceIds.length}</b>. {totalLabel}
                </p>

                {isPartnerTour && pendingPartnerOrder && (
                    <div
                        style={{
                            marginTop: 12,
                            border: "1px solid #334155",
                            borderRadius: 8,
                            padding: 12,
                            background: "#0f172a",
                        }}
                    >
                        <p className="card-text" style={{ margin: 0, color: "#f8fafc" }}>
                            Резерв у партнёра создан: заказ{" "}
                            <b>#{pendingPartnerOrder.order_number}</b>. Места заблокированы у партнёра.
                        </p>
                        <p className="card-text" style={{ marginTop: 8, color: "#f8fafc" }}>
                            Места уже заблокированы у партнёра. Когда получите оплату от клиента,
                            отметьте её обычной кнопкой «Внести оплату» в списке заказов — партнёр
                            получит статус оплаты и билеты автоматически.
                        </p>
                    </div>
                )}

                {!pendingPartnerOrder && (
                <>
                {/* Autocomplete существующего клиента */}
                <Field label="Найти клиента по email" style={{ marginTop: 12 }}>
                    <div style={{ position: "relative" }}>
                        <Input
                            type="text"
                            placeholder="Начните вводить email клиента..."
                            value={emailQuery}
                            onChange={(e) => {
                                setEmailQuery(e.target.value);
                                if (selectedCustomer) setSelectedCustomer(null);
                                setShowDropdown(true);
                            }}
                            autoComplete={"off"}
                            onFocus={() => setShowDropdown(true)}
                        />
                        {selectedCustomer && (
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={handleClearCustomer}
                                style={{ position: "absolute", right: 4, top: 4 }}
                            >
                                ×
                            </Button>
                        )}
                        {showDropdown && emailQuery.trim().length >= 2 && (
                            <div className="admin-order-autocomplete">
                                {emailLoading ? (
                                    <div className="admin-order-autocomplete-empty">
                                        Поиск…
                                    </div>
                                ) : emailResults.length === 0 ? (
                                    <div className="admin-order-autocomplete-empty">
                                        Ничего не найдено
                                    </div>
                                ) : (
                                    emailResults.map((u) => (
                                        <div
                                            key={u.id}
                                            className="admin-order-autocomplete-item"
                                            onClick={() => handlePickCustomer(u)}
                                            onMouseDown={(e) => e.preventDefault()}
                                        >
                                            <div className="admin-order-autocomplete-item-email">
                                                {u.email}
                                            </div>
                                            <div className="admin-order-autocomplete-item-name">
                                                {u.first_name} {u.last_name}
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                    </div>
                </Field>

                {selectedCustomer && (
                    <p
                        style={{
                            color: "#10b981",
                            marginTop: 8,
                            fontSize: 13,
                        }}
                    >
                        Выбран существующий клиент: {selectedCustomer.first_name}{" "}
                        {selectedCustomer.last_name} ({selectedCustomer.email})
                    </p>
                )}

                {!selectedCustomer && (
                    <>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 12,
                                marginTop: 16,
                            }}
                        >
                            <Input
                                type="text"
                                placeholder="Имя (латиница)"
                                value={firstName}
                                onChange={(e) => setFirstName(e.target.value)}
                            />
                            <Input
                                type="text"
                                placeholder="Фамилия (латиница)"
                                value={lastName}
                                onChange={(e) => setLastName(e.target.value)}
                            />
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 12,
                                marginTop: 12,
                            }}
                        >
                            <Input
                                type="email"
                                placeholder="Email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                            />
                            <Input
                                type="tel"
                                placeholder="Телефон (необязательно)"
                                value={phone}
                                onChange={(e) => setPhone(e.target.value)}
                            />
                        </div>
                        <div style={{ marginTop: 12 }}>
                            <Input
                                type="password"
                                placeholder="Пароль (от 8 символов)"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                            />
                        </div>
                    </>
                )}

                {/* Галочка "Пригласительный" */}
                {!isPartnerTour && (
                <label
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 16,
                        cursor: "pointer",
                    }}
                >
                    <input
                        type="checkbox"
                        checked={isInvite}
                        onChange={(e) => setIsInvite(e.target.checked)}
                    />
                    <span>
                        <b>Пригласительный</b> — заказ бесплатный, 100% скидка
                    </span>
                </label>
                )}

                {/* Скидки по билетам ("rabatt") — список открывается в отдельном окне, там он прокрутивается независимо */}
                {canApplyDiscounts && (
                    <div
                        style={{
                            marginTop: 16,
                            border: "1px solid #e5e7eb",
                            borderRadius: 8,
                            padding: 12,
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            gap: 12,
                        }}
                    >
                        <span style={{ fontSize: 13 }}>
                            Скидки на билеты: назначено{" "}
                            <b>{Object.keys(seatDiscounts).length}</b> из{" "}
                            <b>{selectedSeatsInfo?.length ?? 0}</b>
                            {seatDiscountsTotal > 0 && (
                                <span style={{ color: "#10b981" }}>
                                    {" "}
                                    (-{seatDiscountsTotal.toFixed(2)} €)
                                </span>
                            )}
                        </span>
                        <Button
                            type="button"
                            variant="ghost"
                            onClick={() => setIsSeatsDiscountsModalOpen(true)}
                        >
                            Скидки на билеты
                        </Button>
                    </div>
                )}

                {/* Галочка "Нужна доставка" */}
                <label
                    style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        marginTop: 12,
                        cursor: "pointer",
                    }}
                >
                    <input
                        type="checkbox"
                        checked={needDelivery}
                        onChange={(e) => setNeedDelivery(e.target.checked)}
                    />
                    <span>
                        <b>Нужна доставка</b> — билеты высылаются по адресу клиента
                    </span>
                </label>

                {needDelivery && (
                    <div style={{ marginTop: 12 }}>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 12,
                            }}
                        >
                            <Input
                                type="text"
                                placeholder="Страна"
                                value={country}
                                onChange={(e) => setCountry(e.target.value)}
                            />
                            <Input
                                type="text"
                                placeholder="Город"
                                value={city}
                                onChange={(e) => setCity(e.target.value)}
                            />
                        </div>
                        <div
                            style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 2fr",
                                gap: 12,
                                marginTop: 12,
                            }}
                        >
                            <Input
                                type="text"
                                placeholder="Индекс"
                                value={zipCode}
                                onChange={(e) => setZipCode(e.target.value)}
                            />
                            <Input
                                type="text"
                                placeholder="Адрес (улица, дом, квартира)"
                                value={address}
                                onChange={(e) => setAddress(e.target.value)}
                            />
                        </div>
                    </div>
                )}

                {/* Способ оплаты (если не инвайт) */}
                {!isInvite && (
                    <div style={{ marginTop: 12 }}>
                        <Field label="Способ оплаты">
                            <Select
                                value={paymentType}
                                placeholder="Выберите способ оплаты"
                                onChange={(e) =>
                                    setPaymentType(
                                        e.target.value as
                                            | "cash"
                                            | "rechnung"
                                            | "stripe",
                                    )
                                }
                                options={[
                                    { value: "cash", label: "Наличные (вечерняя касса)" },
                                    { value: "rechnung", label: "Счёт на оплату" },
                                    { value: "stripe", label: "Stripe" },
                                ]}
                            />
                        </Field>
                    </div>
                )}
                </>
                )}

                {error && (
                    <p style={{ color: "#ef4444", marginTop: 12 }}>{error}</p>
                )}

                <div className="editor-actions" style={{ marginTop: 24 }}>
                    {isPartnerTour && pendingPartnerOrder ? (
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={handleClosePendingPartnerOrder}
                        >
                            Закрыть
                        </Button>
                    ) : (
                        <>
                            <Button
                                type="button"
                                variant="secondary"
                                onClick={handleSubmit}
                                disabled={isSaving}
                            >
                                {isSaving
                                    ? (isPartnerTour ? "Резервируем..." : "Создаём...")
                                    : (isPartnerTour ? "Зарезервировать у партнёра" : "Создать заказ")}
                            </Button>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={handleCloseOrCancel}
                                disabled={isSaving}
                            >
                                Отмена
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {isSeatsDiscountsModalOpen && (
                <div
                    className="modal-backdrop"
                    style={{ zIndex: 900 }}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Скидки на билеты"
                >
                    <div
                        className="modal"
                        style={{
                            maxWidth: 520,
                            maxHeight: "80vh",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <h3 className="card-title">Скидки на билеты</h3>
                        <p className="card-text" style={{ marginBottom: 8 }}>
                            Выберите скидку для каждого билета отдельно.
                        </p>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                                overflowY: "auto",
                                flex: 1,
                                minHeight: 0,
                                paddingRight: 4,
                            }}
                        >
                            {(selectedSeatsInfo ?? []).map((seat) => {
                                const discountId = seatDiscounts[seat.hall_seat_price_id];
                                const discount = discountId ? discountById.get(discountId) : null;
                                const isLocked = companionLockedSeatId === seat.hall_seat_price_id;
                                return (
                                    <div
                                        key={seat.hall_seat_price_id}
                                        style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            gap: 8,
                                            padding: "6px 0",
                                            borderBottom: "1px solid rgba(148, 163, 184, 0.16)",
                                        }}
                                    >
                                        <span style={{ fontSize: 13 }}>
                                            {seat.label} · {seat.price.toFixed(2)} €
                                            {discount
                                                ? ` — ${discount.name_ru} (-${Number(discount.percent)}%)`
                                                : ""}
                                            {isLocked ? " (сопровождающий, без скидки)" : ""}
                                        </span>
                                        <Button
                                            type="button"
                                            variant="ghost"
                                            disabled={isLocked}
                                            onClick={() =>
                                                setDiscountModalSeatId(seat.hall_seat_price_id)
                                            }
                                        >
                                            {discount ? "изменить" : "выбрать скидку"}
                                        </Button>
                                    </div>
                                );
                            })}
                        </div>
                        {seatDiscountsTotal > 0 && (
                            <p style={{ marginTop: 8, fontSize: 13, color: "#10b981" }}>
                                Итого скидка: -{seatDiscountsTotal.toFixed(2)} €
                            </p>
                        )}
                        <div className="editor-actions" style={{ marginTop: 16 }}>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setIsSeatsDiscountsModalOpen(false)}
                            >
                                Готово
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {discountModalSeatId && (
                <div
                    className="modal-backdrop"
                    style={{ zIndex: 1000 }}
                    role="dialog"
                    aria-modal="true"
                    aria-label="Скидка для билета"
                >
                    <div className="modal" style={{ maxWidth: 420 }}>
                        <h3 className="card-title">Скидка для билета</h3>
                        <p className="card-text">
                            {
                                selectedSeatsInfo?.find(
                                    (s) => s.hall_seat_price_id === discountModalSeatId,
                                )?.label
                            }
                        </p>
                        <div
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 10,
                                marginTop: 12,
                            }}
                        >
                            <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <input
                                    type="radio"
                                    name="admin-seat-discount"
                                    checked={!seatDiscounts[discountModalSeatId]}
                                    onChange={() => handleApplyDiscount(null)}
                                />
                                <span>Без скидки</span>
                            </label>
                            {discounts.map((d) => {
                                const disabled =
                                    Boolean(d.requires_paid_companion) && allSeatIds.length < 2;
                                return (
                                    <label
                                        key={d.id}
                                        style={{
                                            display: "flex",
                                            alignItems: "center",
                                            gap: 8,
                                            opacity: disabled ? 0.5 : 1,
                                        }}
                                    >
                                        <input
                                            type="radio"
                                            name="admin-seat-discount"
                                            disabled={disabled}
                                            checked={seatDiscounts[discountModalSeatId] === d.id}
                                            onChange={() => handleApplyDiscount(d.id)}
                                        />
                                        <span>
                                            {d.name_ru} (-{Number(d.percent)}%)
                                            {d.requires_paid_companion
                                                ? " — требует платного сопровождающего"
                                                : ""}
                                        </span>
                                    </label>
                                );
                            })}
                        </div>
                        <div className="editor-actions" style={{ marginTop: 20 }}>
                            <Button
                                type="button"
                                variant="ghost"
                                onClick={() => setDiscountModalSeatId(null)}
                            >
                                Закрыть
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CreateAdminOrderModal;
